import { useCallback, useEffect, useState } from 'react';

import dayjs from '~/timeInit';

import {
  ANALYTICS_BREAKDOWNS,
  ANALYTICS_COMPARISONS,
  ANALYTICS_PERIODS,
  ANALYTICS_TREND_GRAINS,
  type AnalyticsBreakdown,
  type AnalyticsComparison,
  type AnalyticsDomainSnapshot,
  type AnalyticsPeriod,
  type AnalyticsSnapshotRequest,
  type AnalyticsTrendGrain,
  COMMERCIAL_MARGIN_BASES,
  COMMERCIAL_METRIC_IDS,
  type CommercialMarginBasis,
  type CommercialMetricId,
} from '~/types/leLens';

/**
 * The Commercial page's whole controllable state (01_IMPLEMENTATION_PLAN.md section 1), mirrored
 * to the URL so a view is shareable. Every surface on the page (metric strip, trend, breakdown,
 * Ask bar) reads this one object; nothing keeps its own copy of period/comparison/scope.
 */
export type CommercialPeriod = AnalyticsPeriod | 'last_30_days';

/** The askRichard landing tiles' rolling default. The Commercial area page always opens on Month to date. */
export function defaultCommercialPeriod(now = new Date()): CommercialPeriod {
  return dayjs(now).tz('Australia/Sydney').date() <= 15 ? 'last_30_days' : 'month_to_date';
}

const AREA_PAGE_DEFAULT_PERIOD: CommercialPeriod = 'month_to_date';

export type CommercialViewState = {
  /** BR = before refunds; AR = after refunds. */
  basis: CommercialRevenueBasis;
  /** The Key Tracking Report's Include Margin Overrides parameter. */
  marginBasis: CommercialMarginBasis;
  period: CommercialPeriod;
  from?: string;
  to?: string;
  comparison: AnalyticsComparison;
  comparisonFrom?: string;
  comparisonTo?: string;
  filters: {
    /** accom | bedbank | flights | tours | cruise | experiences | other */
    product: Array<string>;
    /** au | nz | uk | us | sg | hk | other */
    market: Array<string>;
    /** Raw sale_type values, lower-case. No fixed client-side set: options come from the server. */
    saleChannel: Array<string>;
    /**
     * A shareable Accommodation model scope applied from the Products rail. It intentionally
     * remains optional for backward-compatible callers that construct the three older filters.
     * It is valid only alongside the public `product=accommodation` scope.
     */
    accommodationModel?: Array<AccommodationModelId>;
  };
  /** LPP-only KTR subset. Always false outside the exact LPP Accommodation scope. */
  marginMaxedOnly: boolean;
  focusMetric: CommercialMetricId;
  /** Explore (filters, Investigate by, chart, table) is open. Area pages load with it closed. */
  exploreOpen?: boolean;
  /** `all` is the ungrouped overall view; dimensions use governed breakdown ids. */
  split: CommercialInvestigation;
  trendGrain: 'auto' | AnalyticsTrendGrain;
  trendView: 'trend' | 'cumulative' | 'change';
  splitView: 'table' | 'drivers';
  /** Extra metric column pairs shown in the breakdown table, beyond the default TTV column. */
  columns: Array<CommercialColumnId>;
};

export type CommercialInvestigation = AnalyticsBreakdown | 'all';

export const COMMERCIAL_REVENUE_BASES = ['br', 'ar'] as const;
export type CommercialRevenueBasis = (typeof COMMERCIAL_REVENUE_BASES)[number];

/**
 * Public Accommodation model scopes plus non-advertised aliases retained for existing shared links.
 * New navigation only offers Flash, LPP, LPC and aggregate NHA.
 */
export const ACCOMMODATION_MODEL_IDS = ['flash', 'lpp', 'lpc', 'nha', 'lpp_nha', 'lpc_nha'] as const;
export type AccommodationModelId = (typeof ACCOMMODATION_MODEL_IDS)[number];

export const ACCOMMODATION_MODEL_LABELS: Record<AccommodationModelId, string> = {
  flash: 'Flash',
  lpp: 'LPP',
  lpc: 'LPC',
  nha: 'NHA',
  lpp_nha: 'LPP NHA',
  lpc_nha: 'LPC NHA',
};

const BASIS_METRIC_PAIR: Partial<Record<CommercialMetricId, Record<CommercialRevenueBasis, CommercialMetricId>>> = {
  'commercial.ttv_br': { br: 'commercial.ttv_br', ar: 'commercial.ttv_ar' },
  'commercial.ttv_ar': { br: 'commercial.ttv_br', ar: 'commercial.ttv_ar' },
  'commercial.gross_margin_br': { br: 'commercial.gross_margin_br', ar: 'commercial.gross_margin_ar' },
  'commercial.gross_margin_ar': { br: 'commercial.gross_margin_br', ar: 'commercial.gross_margin_ar' },
  'commercial.margin_pct_br': { br: 'commercial.margin_pct_br', ar: 'commercial.margin_pct_ar' },
  'commercial.margin_pct_ar': { br: 'commercial.margin_pct_br', ar: 'commercial.margin_pct_ar' },
  'commercial.aov_br': { br: 'commercial.aov_br', ar: 'commercial.aov_ar' },
  'commercial.aov_ar': { br: 'commercial.aov_br', ar: 'commercial.aov_ar' },
  'commercial.ttv_per_room_night_br': {
    br: 'commercial.ttv_per_room_night_br',
    ar: 'commercial.ttv_per_room_night_ar',
  },
  'commercial.ttv_per_room_night_ar': {
    br: 'commercial.ttv_per_room_night_br',
    ar: 'commercial.ttv_per_room_night_ar',
  },
  'commercial.gm_per_room_night_br': {
    br: 'commercial.gm_per_room_night_br',
    ar: 'commercial.gm_per_room_night_ar',
  },
  'commercial.gm_per_room_night_ar': {
    br: 'commercial.gm_per_room_night_br',
    ar: 'commercial.gm_per_room_night_ar',
  },
  'commercial.adr_br': { br: 'commercial.adr_br', ar: 'commercial.adr_ar' },
  'commercial.adr_ar': { br: 'commercial.adr_br', ar: 'commercial.adr_ar' },
  'commercial.avg_package_value_br': { br: 'commercial.avg_package_value_br', ar: 'commercial.avg_package_value_ar' },
  'commercial.avg_package_value_ar': { br: 'commercial.avg_package_value_br', ar: 'commercial.avg_package_value_ar' },
  'commercial.gm_per_package_br': { br: 'commercial.gm_per_package_br', ar: 'commercial.gm_per_package_ar' },
  'commercial.gm_per_package_ar': { br: 'commercial.gm_per_package_br', ar: 'commercial.gm_per_package_ar' },
  'commercial.ttv_per_pax_br': { br: 'commercial.ttv_per_pax_br', ar: 'commercial.ttv_per_pax_ar' },
  'commercial.ttv_per_pax_ar': { br: 'commercial.ttv_per_pax_br', ar: 'commercial.ttv_per_pax_ar' },
  'commercial.gm_per_pax_br': { br: 'commercial.gm_per_pax_br', ar: 'commercial.gm_per_pax_ar' },
  'commercial.gm_per_pax_ar': { br: 'commercial.gm_per_pax_br', ar: 'commercial.gm_per_pax_ar' },
  'commercial.ttv_per_cruise_night_br': {
    br: 'commercial.ttv_per_cruise_night_br',
    ar: 'commercial.ttv_per_cruise_night_ar',
  },
  'commercial.ttv_per_cruise_night_ar': {
    br: 'commercial.ttv_per_cruise_night_br',
    ar: 'commercial.ttv_per_cruise_night_ar',
  },
  'commercial.revenue_per_view_br': { br: 'commercial.revenue_per_view_br', ar: 'commercial.revenue_per_view_ar' },
  'commercial.revenue_per_view_ar': { br: 'commercial.revenue_per_view_br', ar: 'commercial.revenue_per_view_ar' },
  'commercial.gross_margin_br_overrides': {
    br: 'commercial.gross_margin_br_overrides',
    ar: 'commercial.gross_margin_ar_overrides',
  },
  'commercial.gross_margin_ar_overrides': {
    br: 'commercial.gross_margin_br_overrides',
    ar: 'commercial.gross_margin_ar_overrides',
  },
  'commercial.margin_pct_br_overrides': {
    br: 'commercial.margin_pct_br_overrides',
    ar: 'commercial.margin_pct_ar_overrides',
  },
  'commercial.margin_pct_ar_overrides': {
    br: 'commercial.margin_pct_br_overrides',
    ar: 'commercial.margin_pct_ar_overrides',
  },
  'commercial.margin_per_view_br': { br: 'commercial.margin_per_view_br', ar: 'commercial.margin_per_view_ar' },
  'commercial.margin_per_view_ar': { br: 'commercial.margin_per_view_br', ar: 'commercial.margin_per_view_ar' },
};

const MARGIN_BASIS_METRIC_PAIR: Partial<Record<CommercialMetricId, Record<CommercialMarginBasis, CommercialMetricId>>> =
  {
    'commercial.gross_margin_br': {
      standard: 'commercial.gross_margin_br',
      overrides: 'commercial.gross_margin_br_overrides',
    },
    'commercial.gross_margin_br_overrides': {
      standard: 'commercial.gross_margin_br',
      overrides: 'commercial.gross_margin_br_overrides',
    },
    'commercial.gross_margin_ar': {
      standard: 'commercial.gross_margin_ar',
      overrides: 'commercial.gross_margin_ar_overrides',
    },
    'commercial.gross_margin_ar_overrides': {
      standard: 'commercial.gross_margin_ar',
      overrides: 'commercial.gross_margin_ar_overrides',
    },
    'commercial.margin_pct_br': {
      standard: 'commercial.margin_pct_br',
      overrides: 'commercial.margin_pct_br_overrides',
    },
    'commercial.margin_pct_br_overrides': {
      standard: 'commercial.margin_pct_br',
      overrides: 'commercial.margin_pct_br_overrides',
    },
    'commercial.margin_pct_ar': {
      standard: 'commercial.margin_pct_ar',
      overrides: 'commercial.margin_pct_ar_overrides',
    },
    'commercial.margin_pct_ar_overrides': {
      standard: 'commercial.margin_pct_ar',
      overrides: 'commercial.margin_pct_ar_overrides',
    },
    'commercial.refunds_pct_gm': {
      standard: 'commercial.refunds_pct_gm',
      overrides: 'commercial.refunds_pct_gm_overrides',
    },
    'commercial.refunds_pct_gm_overrides': {
      standard: 'commercial.refunds_pct_gm',
      overrides: 'commercial.refunds_pct_gm_overrides',
    },
  };

/**
 * Keeps the same commercial concept selected while switching the margin basis, the way
 * metricForBasis does for refunds. Anything that does not read margin is unaffected.
 */
export function metricForMarginBasis(id: CommercialMetricId, marginBasis: CommercialMarginBasis): CommercialMetricId {
  return MARGIN_BASIS_METRIC_PAIR[id]?.[marginBasis] ?? id;
}

/** Keeps only the selected member of each standard/override metric pair. */
export function metricBelongsToMarginBasis(id: CommercialMetricId, marginBasis: CommercialMarginBasis): boolean {
  const pair = MARGIN_BASIS_METRIC_PAIR[id];
  return !pair || pair[marginBasis] === id;
}

/** Keeps the same commercial concept selected while switching its refund basis. */
export function metricForBasis(id: CommercialMetricId, basis: CommercialRevenueBasis): CommercialMetricId {
  return BASIS_METRIC_PAIR[id]?.[basis] ?? id;
}

/**
 * Basis-paired metrics resolve to one value; operational/refund metrics appear in both bases.
 *
 * The margin basis is a second, independent axis, so an `_overrides` id carries the refund basis
 * of the id it overrides. Reading the suffix off the raw id would match neither `_br` nor `_ar`
 * and quietly put `gross_margin_ar_overrides` in both bases, showing a duplicate, out-of-basis
 * Gross Margin card.
 */
export function metricBelongsToBasis(id: CommercialMetricId, basis: CommercialRevenueBasis): boolean {
  const refundBasisId = id.endsWith('_overrides') ? id.slice(0, -'_overrides'.length) : id;
  if (refundBasisId.endsWith('_br')) {
    return basis === 'br';
  }
  if (refundBasisId.endsWith('_ar')) {
    return basis === 'ar';
  }
  return true;
}

export const COMMERCIAL_COLUMN_IDS = ['gm', 'margin_pct', 'orders', 'aov', 'refund_rate'] as const;
export type CommercialColumnId = (typeof COMMERCIAL_COLUMN_IDS)[number];

/** The id sets `decodeState` validates `filters.product` / `filters.market` against. */
export const COMMERCIAL_PRODUCT_IDS = [
  'accommodation',
  'accom',
  'bedbank',
  'flights',
  'tours',
  'cruise',
  'experiences',
  'other',
] as const;
export const COMMERCIAL_MARKET_IDS = ['au', 'nz', 'uk', 'us', 'sg', 'hk', 'other'] as const;

export const DEFAULT_STATE: CommercialViewState = {
  basis: 'br',
  marginBasis: 'standard',
  period: 'month_to_date',
  comparison: 'previous_year',
  filters: { product: [], market: [], saleChannel: [] },
  marginMaxedOnly: false,
  focusMetric: 'commercial.ttv_br',
  split: 'all',
  trendGrain: 'auto',
  trendView: 'trend',
  splitView: 'table',
  columns: [],
};

/**
 * Query param scheme. Short keys because these ride alongside the shell's own `view`/`domain`
 * params (see shellUrlState.ts) on every state change. `decodeShellView`/`applyShellView`
 * deliberately do not know about these keys: they pass unrecognised params through untouched,
 * which is what lets this page own its own slice of the URL.
 */
const PARAM = {
  basis: 'basis',
  marginBasis: 'marginBasis',
  period: 'period',
  comparison: 'compare',
  from: 'from',
  to: 'to',
  comparisonFrom: 'cfrom',
  comparisonTo: 'cto',
  product: 'product',
  market: 'market',
  saleChannel: 'channel',
  accommodationModel: 'accommodationModel',
  marginMaxedOnly: 'marginMaxed',
  focusMetric: 'focus',
  exploreOpen: 'explore',
  split: 'split',
  trendGrain: 'grain',
  trendView: 'chart',
  splitView: 'bview',
  columns: 'cols',
} as const;

/** Every query param key this module owns, in encode order. Kept in sync with `PARAM` above by construction. */
export const COMMERCIAL_VIEW_PARAM_KEYS: ReadonlyArray<string> = Object.values(PARAM);

/** The URL key that opens Explore on a Commercial page (value '1'). */
export const COMMERCIAL_EXPLORE_PARAM = PARAM.exploreOpen;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isIsoDate(value: string | null | undefined): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function isOrderedDateRange(from: string | null, to: string | null): from is string {
  return isIsoDate(from) && isIsoDate(to) && from <= to;
}

function splitCsv(value: string | null): Array<string> {
  if (!value) {
    return [];
  }
  return Array.from(
    new Set(
      value
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}

function filterKnown<T extends string>(values: Array<string>, allowed: ReadonlyArray<T>): Array<T> {
  const allowedSet = new Set<string>(allowed);
  return values.filter((value): value is T => allowedSet.has(value));
}

/**
 * Reads a {@link CommercialViewState} out of a query string, tolerantly: a link can be truncated,
 * hand-edited, or carry a preset this build has since dropped, and none of that should throw or
 * leave the page half-configured. An unreadable or unknown value falls back to `DEFAULT_STATE`'s
 * value for that field rather than rejecting the whole state.
 */
export function decodeState(params: URLSearchParams): CommercialViewState {
  const defaultPeriod = AREA_PAGE_DEFAULT_PERIOD;
  const rawMarginBasis = params.get(PARAM.marginBasis);
  const marginBasis: CommercialMarginBasis = (COMMERCIAL_MARGIN_BASES as ReadonlyArray<string>).includes(
    rawMarginBasis ?? '',
  )
    ? (rawMarginBasis as CommercialMarginBasis)
    : DEFAULT_STATE.marginBasis;
  const rawBasis = params.get(PARAM.basis);
  const basis: CommercialRevenueBasis = (COMMERCIAL_REVENUE_BASES as ReadonlyArray<string>).includes(rawBasis ?? '')
    ? (rawBasis as CommercialRevenueBasis)
    : DEFAULT_STATE.basis;
  const rawPeriod = params.get(PARAM.period);
  const decodedPeriod: CommercialPeriod = [...ANALYTICS_PERIODS, 'last_30_days'].includes(rawPeriod as CommercialPeriod)
    ? (rawPeriod as CommercialPeriod)
    : defaultPeriod;

  const rawFrom = params.get(PARAM.from);
  const rawTo = params.get(PARAM.to);
  const hasValidCustomPeriod = decodedPeriod === 'custom' && isOrderedDateRange(rawFrom, rawTo);
  const period = decodedPeriod === 'custom' && !hasValidCustomPeriod ? defaultPeriod : decodedPeriod;

  const rawComparison = params.get(PARAM.comparison);
  const decodedComparison: AnalyticsComparison = (ANALYTICS_COMPARISONS as ReadonlyArray<string>).includes(
    rawComparison ?? '',
  )
    ? (rawComparison as AnalyticsComparison)
    : DEFAULT_STATE.comparison;

  const rawComparisonFrom = params.get(PARAM.comparisonFrom);
  const rawComparisonTo = params.get(PARAM.comparisonTo);
  const hasValidCustomComparison =
    decodedComparison === 'custom' && isOrderedDateRange(rawComparisonFrom, rawComparisonTo);
  const comparison =
    decodedComparison === 'custom' && !hasValidCustomComparison ? DEFAULT_STATE.comparison : decodedComparison;

  const rawFocusMetric = params.get(PARAM.focusMetric);
  const decodedFocusMetric: CommercialMetricId = (COMMERCIAL_METRIC_IDS as ReadonlyArray<string>).includes(
    rawFocusMetric ?? '',
  )
    ? (rawFocusMetric as CommercialMetricId)
    : DEFAULT_STATE.focusMetric;
  const rawSplit = params.get(PARAM.split);
  const split: CommercialInvestigation = (ANALYTICS_BREAKDOWNS as ReadonlyArray<string>).includes(rawSplit ?? '')
    ? (rawSplit as AnalyticsBreakdown)
    : DEFAULT_STATE.split;

  const rawTrendGrain = params.get(PARAM.trendGrain);
  const trendGrain: CommercialViewState['trendGrain'] =
    rawTrendGrain === 'auto' || (ANALYTICS_TREND_GRAINS as ReadonlyArray<string>).includes(rawTrendGrain ?? '')
      ? (rawTrendGrain as CommercialViewState['trendGrain'])
      : DEFAULT_STATE.trendGrain;

  const rawTrendView = params.get(PARAM.trendView);
  const trendView: CommercialViewState['trendView'] =
    rawTrendView === 'trend' || rawTrendView === 'cumulative' || rawTrendView === 'change'
      ? rawTrendView
      : DEFAULT_STATE.trendView;

  const rawSplitView = params.get(PARAM.splitView);
  const splitView: CommercialViewState['splitView'] =
    rawSplitView === 'table' || rawSplitView === 'drivers' ? rawSplitView : DEFAULT_STATE.splitView;

  const product = filterKnown(
    splitCsv(params.get(PARAM.product)).map((value) => (value === 'cruises' ? 'cruise' : value)),
    COMMERCIAL_PRODUCT_IDS,
  );
  const accommodationModel = isExactAccommodationScope(product)
    ? filterKnown(splitCsv(params.get(PARAM.accommodationModel)), ACCOMMODATION_MODEL_IDS)
    : [];
  const marginMaxedOnly =
    params.get(PARAM.marginMaxedOnly) === 'true' && isMarginMaxedScope(product, accommodationModel);
  const focusMetric = marginMaxedOnly ? 'commercial.gross_margin_br' : metricForBasis(decodedFocusMetric, basis);

  return {
    basis: marginMaxedOnly ? 'br' : basis,
    marginBasis,
    period,
    ...(hasValidCustomPeriod ? { from: rawFrom, to: rawTo } : {}),
    comparison,
    ...(hasValidCustomComparison ? { comparisonFrom: rawComparisonFrom, comparisonTo: rawComparisonTo } : {}),
    filters: {
      product,
      market: filterKnown(splitCsv(params.get(PARAM.market)), COMMERCIAL_MARKET_IDS),
      // No fixed client-side set: the server is the source of truth for which sale_channel ids exist.
      saleChannel: splitCsv(params.get(PARAM.saleChannel)),
      ...(accommodationModel.length ? { accommodationModel } : {}),
    },
    marginMaxedOnly,
    focusMetric,
    ...(params.get(PARAM.exploreOpen) === '1' ? { exploreOpen: true } : {}),
    split,
    trendGrain,
    trendView,
    splitView,
    columns: filterKnown(splitCsv(params.get(PARAM.columns)), COMMERCIAL_COLUMN_IDS),
  };
}

/**
 * The params this state should carry, as entries to apply over whatever else is in the address
 * (the pattern `shellUrlState.ts`'s `shellViewParams` uses). A null value means "remove this key":
 * a field left at its default keeps the URL clean rather than restating every default on load.
 */
export function encodeState(state: CommercialViewState): Array<[string, string | null]> {
  const isCustomPeriod = state.period === 'custom' && state.from && state.to;
  const isCustomComparison = state.comparison === 'custom' && state.comparisonFrom && state.comparisonTo;
  return [
    [PARAM.basis, state.basis === DEFAULT_STATE.basis ? null : state.basis],
    [PARAM.marginBasis, state.marginBasis === DEFAULT_STATE.marginBasis ? null : state.marginBasis],
    [PARAM.period, state.period === DEFAULT_STATE.period ? null : state.period],
    [PARAM.from, isCustomPeriod ? (state.from ?? null) : null],
    [PARAM.to, isCustomPeriod ? (state.to ?? null) : null],
    [PARAM.comparison, state.comparison === DEFAULT_STATE.comparison ? null : state.comparison],
    [PARAM.comparisonFrom, isCustomComparison ? (state.comparisonFrom ?? null) : null],
    [PARAM.comparisonTo, isCustomComparison ? (state.comparisonTo ?? null) : null],
    [PARAM.product, state.filters.product.length ? state.filters.product.join(',') : null],
    [PARAM.market, state.filters.market.length ? state.filters.market.join(',') : null],
    [PARAM.saleChannel, state.filters.saleChannel.length ? state.filters.saleChannel.join(',') : null],
    [
      PARAM.accommodationModel,
      state.filters.accommodationModel?.length ? state.filters.accommodationModel.join(',') : null,
    ],
    [
      PARAM.marginMaxedOnly,
      state.marginMaxedOnly && isMarginMaxedScope(state.filters.product, state.filters.accommodationModel)
        ? 'true'
        : null,
    ],
    [PARAM.focusMetric, state.focusMetric === DEFAULT_STATE.focusMetric ? null : state.focusMetric],
    [PARAM.exploreOpen, state.exploreOpen ? '1' : null],
    // All is the default overall view. Omit it so links do not claim a dimension is selected.
    [PARAM.split, state.split === 'all' ? null : state.split],
    [PARAM.trendGrain, state.trendGrain === DEFAULT_STATE.trendGrain ? null : state.trendGrain],
    [PARAM.trendView, state.trendView === DEFAULT_STATE.trendView ? null : state.trendView],
    [PARAM.splitView, state.splitView === DEFAULT_STATE.splitView ? null : state.splitView],
    [PARAM.columns, state.columns.length ? state.columns.join(',') : null],
  ];
}

const AUTO_GRAIN_BY_PERIOD: Partial<Record<CommercialPeriod, AnalyticsTrendGrain>> = {
  yesterday: 'day',
  week_to_date: 'day',
  month_to_date: 'day',
  last_7_days: 'day',
  last_14_days: 'day',
  last_28_days: 'day',
  last_30_days: 'day',
  quarter_to_date: 'week',
  last_90_days: 'week',
  year_to_date: 'month',
  last_12_months: 'month',
};

/**
 * The auto grain rule (01_IMPLEMENTATION_PLAN.md section 1): MTD/WTD/yesterday/last_7/28 -> day;
 * QTD/last_90 -> week; YTD/last_12_months -> month. A user override in `trendGrain` always wins.
 * `custom` has no fixed mapping in the plan, so it falls back to the span of the chosen dates.
 */
export function resolveTrendGrain(state: CommercialViewState): AnalyticsTrendGrain {
  if (state.trendGrain !== 'auto') {
    return state.trendGrain;
  }
  const fixed = AUTO_GRAIN_BY_PERIOD[state.period];
  if (fixed) {
    return fixed;
  }
  if (state.period === 'custom' && state.from && state.to) {
    const days =
      Math.round(
        (new Date(`${state.to}T00:00:00Z`).getTime() - new Date(`${state.from}T00:00:00Z`).getTime()) / 86_400_000,
      ) + 1;
    if (days <= 35) {
      return 'day';
    }
    if (days <= 180) {
      return 'week';
    }
    return 'month';
  }
  return 'day';
}

/** Builds the request `useGetAnalyticsDomainSnapshot`/`useGetAnalyticsDomainBreakdown` send. */
export function stateToSnapshotRequest(state: CommercialViewState, now = new Date()): AnalyticsSnapshotRequest {
  const today = dayjs(now).tz('Australia/Sydney').startOf('day');
  const rolling30 = state.period === 'last_30_days';
  return {
    period: state.period === 'last_30_days' ? 'custom' : state.period,
    from: rolling30 ? today.subtract(30, 'day').format('YYYY-MM-DD') : state.from,
    to: rolling30 ? today.subtract(1, 'day').format('YYYY-MM-DD') : state.to,
    comparison: state.comparison,
    comparisonFrom: state.comparisonFrom,
    comparisonTo: state.comparisonTo,
    // Snapshot requests always resolve their own product breakdown. The dedicated split endpoint
    // is disabled for All by the page, so this fallback is never presented as a selected split.
    breakdown: state.split === 'all' ? 'product' : state.split,
    // Keep the public Accommodation identifier intact. Reporting owns its declared
    // scope (direct accommodation plus LPP/LTLP); collapsing it to legacy `accom`
    // here would silently exclude partner accommodation before either source runs.
    product: state.filters.product.map((product) => (product === 'cruises' ? 'cruise' : product)),
    market: state.filters.market,
    saleChannel: state.filters.saleChannel,
    accommodationModel: state.filters.accommodationModel,
    marginMaxedOnly:
      state.marginMaxedOnly && isMarginMaxedScope(state.filters.product, state.filters.accommodationModel)
        ? true
        : undefined,
    // Omit when auto: the server applies the same rule itself from `period`.
    trendGrain: state.trendGrain === 'auto' ? undefined : state.trendGrain,
    // Omit the standard basis so an existing link keeps its current numbers.
    marginBasis: state.marginBasis === 'standard' ? undefined : state.marginBasis,
  };
}

const COMMERCIAL_METRIC_NAMES: Record<CommercialMetricId, string> = {
  'commercial.refunds_pct_gm': 'Refunds %',
  'commercial.refunds_pct_gm_overrides': 'Refunds % (overrides)',
  'commercial.gross_margin_br_overrides': 'Gross Margin (BR, overrides)',
  'commercial.gross_margin_ar_overrides': 'Gross Margin (AR, overrides)',
  'commercial.margin_pct_br_overrides': 'Margin % (BR, overrides)',
  'commercial.margin_pct_ar_overrides': 'Margin % (AR, overrides)',
  'commercial.ttv_br': 'TTV (BR)',
  'commercial.gross_margin_br': 'Gross Margin (BR)',
  'commercial.margin_pct_br': 'Margin % (BR)',
  'commercial.ttv_ar': 'TTV (AR)',
  'commercial.gross_margin_ar': 'Gross Margin (AR)',
  'commercial.margin_pct_ar': 'Margin % (AR)',
  'commercial.orders': 'Orders',
  'commercial.aov_br': 'AOV (BR)',
  'commercial.aov_ar': 'AOV (AR)',
  'commercial.refund_rate': 'Refund Rate',
  'commercial.refunds': 'Refunds',
  'commercial.room_nights': 'Room Nights',
  'commercial.cruise_nights': 'Cruise Nights',
  'commercial.pax': 'Pax',
  'commercial.total_pax': 'Pax',
  'commercial.adr_br': 'ADR (BR)',
  'commercial.adr_ar': 'ADR (AR)',
  'commercial.avg_package_value_br': 'Avg Package Value (BR)',
  'commercial.avg_package_value_ar': 'Avg Package Value (AR)',
  'commercial.gm_per_package_br': 'Margin per Package (BR)',
  'commercial.gm_per_package_ar': 'Margin per Package (AR)',
  'commercial.ttv_per_pax_br': 'TTV per Pax (BR)',
  'commercial.ttv_per_pax_ar': 'TTV per Pax (AR)',
  'commercial.gm_per_pax_br': 'Margin per Pax (BR)',
  'commercial.gm_per_pax_ar': 'Margin per Pax (AR)',
  'commercial.ttv_per_cruise_night_br': 'TTV per Cruise Night (BR)',
  'commercial.ttv_per_cruise_night_ar': 'TTV per Cruise Night (AR)',
  'commercial.ttv_per_room_night_br': 'ADR (BR)',
  'commercial.ttv_per_room_night_ar': 'ADR (AR)',
  'commercial.gm_per_room_night_br': 'GM / Room Night (BR)',
  'commercial.gm_per_room_night_ar': 'GM / Room Night (AR)',
  'commercial.total_views': 'Total Views',
  'commercial.unique_views': 'Unique Views',
  'commercial.packages': 'Packages',
  'commercial.packages_attributed': 'Attributed Packages',
  'commercial.conversion_rate': 'Offer conversion',
  'commercial.items': 'Items',
  'commercial.add_to_cart_rate': 'Add to Cart Rate',
  'commercial.revenue_per_view': 'Revenue per View',
  'commercial.revenue_per_view_br': 'Revenue per View (BR)',
  'commercial.revenue_per_view_ar': 'Revenue per View (AR)',
  'commercial.margin_per_view': 'Margin per View',
  'commercial.margin_per_view_br': 'Margin per View (BR)',
  'commercial.margin_per_view_ar': 'Margin per View (AR)',
  'commercial.margin_per_view_luxplus': 'Margin per View (LuxPlus)',
  'commercial.ttv_br_attributed': 'TTV (BR, attributed)',
  'commercial.ttv_ar_attributed': 'TTV (AR, attributed)',
  'commercial.gross_margin_br_attributed': 'Gross Margin (BR, attributed)',
  'commercial.gross_margin_ar_attributed': 'Gross Margin (AR, attributed)',
};

const PERIOD_WORDS: Record<CommercialPeriod, string> = {
  yesterday: 'yesterday',
  week_to_date: 'week to date',
  month_to_date: 'month to date',
  quarter_to_date: 'quarter to date',
  year_to_date: 'year to date',
  last_7_days: 'the last 7 days',
  last_14_days: 'the last 14 days',
  last_28_days: 'the last 28 days',
  last_30_days: 'the last 30 days',
  last_90_days: 'the last 90 days',
  last_12_months: 'the last 12 months',
  custom: 'a custom range',
};

const COMPARISON_WORDS: Record<AnalyticsComparison, string> = {
  previous_year: 'the same dates last year',
  previous_period: 'the previous period',
  custom: 'a custom comparison range',
  none: 'no comparison',
};

const BREAKDOWN_WORDS: Record<AnalyticsBreakdown, string> = {
  product: 'Product',
  experience_provider: 'Experience provider',
  accommodation_type: 'Accommodation model',
  length_of_stay: 'Length of stay',
  booking_lead_time: 'Booking lead time',
  market: 'Market',
  sale_channel: 'Sale channel',
  platform: 'Platform',
  customer_type: 'New vs repeat',
  domestic_international: 'Domestic vs international',
  customer_country: 'Customer country',
  destination_region: 'Destination region',
  margin_max_rebooking: 'Margin Max rebooking',
};

function titleCase(value: string): string {
  return value.replace(
    /(^|[\s/_-])([a-z])/g,
    (_match, boundary: string, letter: string) => `${boundary}${letter.toUpperCase()}`,
  );
}

/**
 * One plain-English paragraph describing `state`, sent as hidden context on the Ask bar (extends
 * `handleAskDomain`'s existing prompt string). Pure over `state` alone: it cannot know the server's
 * resolved from/to for a named preset (that is deliberately server-owned, see FE_MAP.md section
 * 2.2), so a preset period reads as words ("month to date") rather than dates. Pass `resolved` (the
 * live snapshot's `period`) when it is available, e.g. from the Ask bar composing against an
 * already-loaded page, to get the fuller "1-10 Sep 2026" form the design brief shows.
 */
export function stateToAskContext(
  state: CommercialViewState,
  resolved?: { from: string; to: string; comparisonFrom?: string; comparisonTo?: string },
): string {
  const periodPhrase =
    state.period === 'custom' && state.from && state.to
      ? `${state.from} to ${state.to}`
      : resolved
        ? `${PERIOD_WORDS[state.period]}, ${resolved.from} to ${resolved.to}`
        : PERIOD_WORDS[state.period];

  const comparisonPhrase =
    state.comparison === 'none'
      ? null
      : state.comparison === 'custom' && state.comparisonFrom && state.comparisonTo
        ? `compared with ${state.comparisonFrom} to ${state.comparisonTo}`
        : resolved?.comparisonFrom && resolved?.comparisonTo
          ? `compared with ${resolved.comparisonFrom} to ${resolved.comparisonTo}`
          : `compared with ${COMPARISON_WORDS[state.comparison]}`;

  const scopeParts = [
    state.filters.product.length ? `Product = ${state.filters.product.map(titleCase).join(', ')}` : null,
    state.filters.market.length ? `Market = ${state.filters.market.map((id) => id.toUpperCase()).join(', ')}` : null,
    state.filters.saleChannel.length ? `Sale channel = ${state.filters.saleChannel.map(titleCase).join(', ')}` : null,
    state.filters.accommodationModel?.length
      ? `Accommodation model = ${state.filters.accommodationModel
          .map((model) => ACCOMMODATION_MODEL_LABELS[model])
          .join(', ')}`
      : null,
    state.marginMaxedOnly ? 'Margin Maxed only' : null,
  ].filter((part): part is string => Boolean(part));

  const sentences = [
    `Reporting period ${periodPhrase}${comparisonPhrase ? `, ${comparisonPhrase}` : ''}.`,
    `Revenue basis: ${state.basis === 'ar' ? 'after refunds (AR)' : 'before refunds (BR)'}.`,
    scopeParts.length ? `Scope: ${scopeParts.join('; ')}.` : 'Scope: all products, markets and sale channels.',
    `Focus metric: ${COMMERCIAL_METRIC_NAMES[state.focusMetric]}.`,
    state.split === 'all'
      ? `Visible analysis: overall total; ${state.trendGrain} grain, ${state.trendView} chart.`
      : `Visible analysis: ${BREAKDOWN_WORDS[state.split]} breakdown in ${state.splitView} view; ${state.trendGrain} grain, ${state.trendView} chart.`,
  ];
  return sentences.join(' ');
}

export function isExactAccommodationScope(product: ReadonlyArray<string>): boolean {
  return product.length === 1 && product[0] === 'accommodation';
}

/** Margin Maxed is a governed LPP-only slice, not a general Accommodation filter. */
export function isMarginMaxedScope(
  product: ReadonlyArray<string>,
  accommodationModel: ReadonlyArray<AccommodationModelId> | undefined,
): boolean {
  return isExactAccommodationScope(product) && accommodationModel?.length === 1 && accommodationModel[0] === 'lpp';
}

const PRODUCT_SCOPED_BREAKDOWNS: ReadonlySet<AnalyticsBreakdown> = new Set([
  'accommodation_type',
  'length_of_stay',
  'booking_lead_time',
  'domestic_international',
  'customer_country',
  'destination_region',
  'platform',
]);

/** Keeps the LPP-only KTR slice honest when another control or navigation preset changes scope. */
export function normalizeCommercialState(
  state: CommercialViewState,
  previous?: CommercialViewState,
): CommercialViewState {
  const leftProductScope =
    Boolean(previous?.filters.product.length) &&
    state.filters.product.length === 0 &&
    state.split !== 'all' &&
    PRODUCT_SCOPED_BREAKDOWNS.has(state.split);
  const accommodationModel = isExactAccommodationScope(state.filters.product)
    ? (state.filters.accommodationModel ?? [])
    : [];
  const marginMaxedOnly = state.marginMaxedOnly && isMarginMaxedScope(state.filters.product, accommodationModel);
  const accommodationModelChanged =
    accommodationModel.length !== (state.filters.accommodationModel?.length ?? 0) ||
    accommodationModel.some((model, index) => model !== state.filters.accommodationModel?.[index]);
  const basis = marginMaxedOnly ? 'br' : state.basis;
  const enablingMarginMaxed = marginMaxedOnly && previous?.marginMaxedOnly !== true;
  const focusMetric = marginMaxedOnly
    ? enablingMarginMaxed
      ? 'commercial.gross_margin_br'
      : metricForBasis(state.focusMetric, 'br')
    : state.focusMetric;
  if (
    marginMaxedOnly === state.marginMaxedOnly &&
    basis === state.basis &&
    focusMetric === state.focusMetric &&
    !leftProductScope &&
    !accommodationModelChanged
  ) {
    return state;
  }
  return {
    ...state,
    filters: {
      ...state.filters,
      ...(accommodationModel.length ? { accommodationModel } : { accommodationModel: undefined }),
    },
    basis,
    focusMetric,
    marginMaxedOnly,
    split: leftProductScope ? 'all' : state.split,
  };
}

/**
 * Changes to what the figures cover (period, comparison, basis, filters) are steps the reader can go Back from.
 * Opening or closing Explore and choosing its subject or split only change what is shown of the same figures, so they replace.
 */
export function historyModeFor(current: CommercialViewState, next: CommercialViewState): 'push' | 'replace' {
  const scopeOf = ({
    exploreOpen: _exploreOpen,
    focusMetric: _focusMetric,
    split: _split,
    trendGrain: _trendGrain,
    trendView: _trendView,
    splitView: _splitView,
    columns: _columns,
    ...scope
  }: CommercialViewState) => JSON.stringify(scope);
  return scopeOf(current) === scopeOf(next) ? 'replace' : 'push';
}

/**
 * `state` written over `params`, leaving every default off. The one exception is a period the address already
 * spells out, which a page opened from Home is seeded with: that stays, so the link keeps naming its period.
 */
export function paramsWithState(params: URLSearchParams, state: CommercialViewState): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [key, value] of encodeState(state)) {
    if (value !== null) {
      next.set(key, value);
    } else if (key !== PARAM.period || next.get(key) !== DEFAULT_STATE.period) {
      next.delete(key);
    }
  }
  return next;
}

function currentSearchParams(): URLSearchParams {
  return new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
}

/**
 * Syncs {@link CommercialViewState} with the URL without a full navigation. Period, comparison,
 * basis and filter changes become their own browser-history entries (see {@link historyModeFor});
 * Explore's own state replaces the current entry. Only
 * this module's own keys (`COMMERCIAL_VIEW_PARAM_KEYS`) are ever touched, so the shell's
 * `view`/`domain` params and any other page's params survive untouched.
 */
export function useCommercialViewState(): {
  state: CommercialViewState;
  update: (partial: Partial<CommercialViewState>) => void;
} {
  const [state, setState] = useState<CommercialViewState>(() => decodeState(currentSearchParams()));

  const writeState = useCallback((next: CommercialViewState, history: 'push' | 'replace') => {
    if (typeof window === 'undefined') {
      return;
    }
    const search = paramsWithState(currentSearchParams(), next).toString();
    const target = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (target !== current) {
      if (history === 'push') {
        window.history.pushState(window.history.state, '', target);
      } else {
        window.history.replaceState(window.history.state, '', target);
      }
      // The Commercial page owns this URL state, while the shell owns the active rail selection.
      // Native pushState/replaceState does not notify the shell, so publish the same local
      // navigation signal after a page-owned change (notably clearing a model leaf scope).
      window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
    }
  }, []);

  const update = useCallback(
    (partial: Partial<CommercialViewState>) => {
      setState((current) => {
        const next = normalizeCommercialState(
          {
            ...current,
            ...partial,
            filters: { ...current.filters, ...partial.filters },
          },
          current,
        );
        writeState(next, historyModeFor(current, next));
        return next;
      });
    },
    [writeState],
  );

  // Settles the address on load: unreadable params drop out, and a default is not spelled in.
  useEffect(() => writeState(state, 'replace'), [state, writeState]);

  // Browser back/forward: pick the state back up from wherever navigation landed.
  useEffect(() => {
    const onPopState = () => setState(decodeState(currentSearchParams()));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return { state, update };
}

/** Convenience for callers that already have a loaded snapshot and want the live period for context. */
export function snapshotToResolvedPeriod(
  snapshot: AnalyticsDomainSnapshot | undefined,
): { from: string; to: string; comparisonFrom?: string; comparisonTo?: string } | undefined {
  if (!snapshot) {
    return undefined;
  }
  return {
    from: snapshot.period.from,
    to: snapshot.period.to,
    comparisonFrom: snapshot.period.comparison?.from,
    comparisonTo: snapshot.period.comparison?.to,
  };
}
